import { __rest } from "tslib";
import { useEffect } from 'react';
import useCreateBuildWithAIRevisionPredictionAsync from './useCreateBuildWithAIRevisionPredictionAsync/useCreateBuildWithAIRevisionPredictionAsync';
import useGetBuildWithAIRevisionAsync from './useGetBuildWithAIRevisionAsync/useGetBuildWithAIRevisionAsync';
const PollInterval = 1000;
/**
 * Hook for getting a Build with AI survey revision based on an existing survey
 * and a user prompt. Prompt history is also included to give context to the
 * model.
 */
const useBuildWithAIRevision = ({
  onSurveyRevision
} = {}) => {
  var _a, _b;
  const [createPrediction, {
    called: createPredictionCalled,
    loading: createPredictionLoading,
    error: createPredictionError,
    data: createPredictionData
  }] = useCreateBuildWithAIRevisionPredictionAsync();
  const mutate = (userInput, history, survey) => {
    // Do nothing on error (find error declaratively)
    void createPrediction({
      variables: {
        userInput,
        history,
        survey
      }
    }).catch(_ => {});
  };
  // Get prediction id to call query to get the prediction
  const predictionId = (_a = createPredictionData === null || createPredictionData === void 0 ? void 0 : createPredictionData.createBuildWithAIRevisionPredictionAsync) === null || _a === void 0 ? void 0 : _a.predictionId;
  const {
    error: getPredictionError,
    data: getPredictionData,
    loading: getPredictionLoading,
    startPolling,
    stopPolling
  } = useGetBuildWithAIRevisionAsync(predictionId, {
    notifyOnNetworkStatusChange: true,
    onCompleted: data => {
      var _a;
      if (((_a = data === null || data === void 0 ? void 0 : data.getBuildWithAIRevisionAsync) === null || _a === void 0 ? void 0 : _a.__typename) === 'GetBuildWithAIRevisionAsyncResponse') {
        onSurveyRevision === null || onSurveyRevision === void 0 ? void 0 : onSurveyRevision(data.getBuildWithAIRevisionAsync);
      }
    }
  });
  const insightResponseType = (_b = getPredictionData === null || getPredictionData === void 0 ? void 0 : getPredictionData.getBuildWithAIRevisionAsync) === null || _b === void 0 ? void 0 : _b.__typename;
  const shouldPoll = predictionId && insightResponseType === 'PredictionNotFoundError';
  useEffect(() => {
    if (shouldPoll) {
      startPolling(PollInterval);
      return () => {
        stopPolling();
      };
    }
  }, [shouldPoll, startPolling, stopPolling]);
  if (!createPredictionCalled) {
    return [mutate, {
      state: 'uncalled'
    }];
  }
  if (createPredictionLoading || getPredictionLoading || shouldPoll) {
    return [mutate, {
      state: 'loading'
    }];
  }
  if (createPredictionError) {
    return [mutate, {
      state: 'error',
      error: createPredictionError
    }];
  }
  if (!predictionId) {
    return [mutate, {
      state: 'error',
      error: 'ResolverError'
    }];
  }
  if (getPredictionError) {
    return [mutate, {
      state: 'error',
      error: getPredictionError
    }];
  }
  if (!getPredictionData) {
    // This should never happen because `loading` and `error` are already known
    // not to be set; does happen sometimes when the schema and the query are
    // out of sync or fragment matcher doesn't know about unions.
    return [mutate, {
      state: 'error',
      error: 'NoDataReturned'
    }];
  }
  if (getPredictionData.getBuildWithAIRevisionAsync.__typename !== 'GetBuildWithAIRevisionAsyncResponse') {
    return [mutate, {
      state: 'error',
      error: getPredictionData.getBuildWithAIRevisionAsync
    }];
  }
  const _c = getPredictionData.getBuildWithAIRevisionAsync,
    {
      __typename: _
    } = _c,
    successResponse = __rest(_c, ["__typename"]);
  return [mutate, Object.assign({
    state: 'success'
  }, successResponse)];
};
export default useBuildWithAIRevision;